import type { CrewEntry, CrewKey, World, WorldPage } from "../crewData";

/**
 * Czech crew for bidmio.cz: the same fourteen trades and figures as Slovakia,
 * with lines written for Czech customers. Every answer names something the app
 * does (cash forecast, advance invoices, QR Platba, material and hours per job,
 * leave calendar, machine cards, reminders from receivables). The joiner is
 * paid by invoice after the fitting, never on site: from 2027 an on-site
 * payment is an EET 2.0 contact payment.
 */
export const CS_CREW: Record<CrewKey, CrewEntry> = {
  mason: {
    label: "Zedník",
    home: "survive",
    survive: { quote: "Na výplaty zase nezbylo.", answer: "Dopředu uvidíte, kdy budou chybět peníze. A kdo vám ještě dluží." },
    grow: { quote: "Tři stavby najednou a nevím, kde mám lidi.", answer: "Denní plán v mobilu každého. Ráno nikdo nevolá." },
  },
  carpenter: {
    label: "Tesař",
    home: "grow",
    survive: { quote: "Řezivo jsem zaplatil já. Zákazník zatím nic.", answer: "Zálohovou fakturu pošlete před začátkem. Řezivo zaplatí zákazník, ne vy." },
    grow: { quote: "Práce mám na půl roku. Nestíhám.", answer: "Party, stavby a termíny na jednom místě. Bez telefonování." },
  },
  electrician: {
    label: "Elektrikář",
    home: "survive",
    survive: { quote: "Levnější mě podstřelil o třetinu.", answer: "Profesionální nabídku pošlete ještě od zákazníka. Kdo se bojí fušera, vybere si vás." },
    grow: { quote: "Kam zmizelo 40 metrů kabelu?", answer: "Každý metr zapsaný na zakázku. Hned vidíte, kde chybí." },
  },
  plumber: {
    label: "Instalatér",
    home: "grow",
    survive: { quote: "Havárie v noci. Faktura nikdy.", answer: "Výjezd zapíšete v noci v mobilu. Ráno z něj je faktura." },
    grow: { quote: "Montáže, servis, lidi. Všechno v hlavě.", answer: "Montáže i servis v jednom plánu. Hlava je volná." },
  },
  painter: {
    label: "Malíř",
    home: "survive",
    survive: { quote: "Dva měsíce čekám na peníze.", answer: "Faktura s QR kódem hned po práci. A vidíte, kdo vám dluží." },
    grow: { quote: "Parta maluje a já ani nevím kde.", answer: "Každý vidí svou stavbu, vy všechny najednou." },
  },
  roofer: {
    label: "Pokrývač",
    home: "grow",
    survive: { quote: "Prší, parta stojí, výplaty běží.", answer: "Dopředu uvidíte, který týden bude těžký. Máte čas zareagovat." },
    grow: { quote: "Dva jsou na neschopence a střecha stojí.", answer: "Dovolené a neschopenky v kalendáři. Vidíte, koho kam přesunout." },
  },
  service: {
    label: "Servisák",
    home: "survive",
    survive: { quote: "Tenhle týden nemám kam jet.", answer: "Všichni zákazníci i s historií na jednom místě. Víte, komu zavolat." },
    grow: { quote: "Šest techniků a ze mě je dispečer.", answer: "Plán výjezdů, protokoly a faktury z mobilu." },
  },
  siteManager: {
    label: "Stavbyvedoucí",
    home: "grow",
    survive: { quote: "Investor platí o dva měsíce později.", answer: "Vidíte, kdo vám kolik dluží a kdy bude těsno." },
    grow: { quote: "Obrat roste. Peněz ubývá.", answer: "Zisk každé stavby, ne jen obrat." },
  },
  owner: {
    label: "Majitel firmy",
    home: "grow",
    survive: { quote: "Investor nezaplatil a výplaty mám pro třicet lidí.", answer: "Kdo vám kolik dluží, i s upomínkami. Víte, komu volat první." },
    grow: { quote: "Čtyřicet lidí a nevím, která stavba vydělává.", answer: "Zisk každé stavby a kdo kde dnes dělá. Na jednom místě." },
  },
  tiler: {
    label: "Obkladač",
    home: "survive",
    survive: { quote: "Dražší obklad jsem doplatil já.", answer: "Změnu zapíšete hned i s cenou. Do faktury se propíše sama." },
    grow: { quote: "Tři byty najednou, materiál všude.", answer: "Materiál na konkrétní byt. Nic se neztratí." },
  },
  joiner: {
    label: "Truhlář",
    home: "grow",
    survive: { quote: "Kuchyň hotová. Doplatek nikde.", answer: "Záloha při objednávce, faktura na doplatek hned po montáži." },
    grow: { quote: "Dílna, montáže a já jsem jeden.", answer: "Hodiny v dílně i na montáži u každé zakázky." },
  },
  facade: {
    label: "Fasádník",
    home: "survive",
    survive: { quote: "Vzal jsem to pod cenou. Leasing nepočká.", answer: "Podívejte se nejdřív, kolik vám dluží. Často je to víc, než chybí na leasing." },
    grow: { quote: "Lešení je zase na jiné stavbě.", answer: "Lešení a stroje evidované na stavbě, kde právě stojí." },
  },
  digger: {
    label: "Bagrista",
    home: "grow",
    survive: { quote: "Bagr stál celý týden.", answer: "Prázdný týden uvidíte v plánu dřív. Máte čas obvolat zákazníky." },
    grow: { quote: "Motohodiny bagru nikdo nezapsal.", answer: "Hodiny zapíšete na zakázku z mobilu. Nic nevypadne z faktury." },
  },
  developer: {
    label: "Developer",
    home: "grow",
    grow: {
      quote: "Tři projekty, deset subdodavatelů. Rozpočet utíká.",
      answer: "Rozpočet, změny a skutečné náklady u každého projektu. Odchylku vidíte průběžně, ne až při kolaudaci.",
    },
  },
};

/** The two Czech world pages and how the home page names them. */
export const CS_WORLD_PAGES: Record<World, WorldPage> = {
  survive: {
    href: "/kdyz-chybi-prace-a-penize/",
    door: "Tohle jsem já: chybí práce a peníze",
    title: "Dělal bych. Chybí práce a peníze.",
  },
  grow: {
    href: "/kdyz-chybi-poradek/",
    door: "Tohle jsem já: chybí pořádek",
    title: "Práce je. Chybí pořádek.",
  },
};
